/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1753142400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 22 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adobe-experience-platform",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Adobe Experience Platform",
          "depth": 0,
          "id": "connect-to-adobe-experience-platform"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing datasets",
          "depth": 1,
          "id": "syncing-datasets"
        }, {
          "value": "Trigger campaigns via Adobe Journey Optimizer",
          "depth": 1,
          "id": "trigger-campaigns-via-adobe-journey-optimizer"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Datasets"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to dataset records"
            }), _jsx(_components.td, {
              children: "Upsert, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.adobe.com/experience-platform-apis/references/batch-ingestion/#operation/createBatch",
                children: "Datasets docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Adobe Journey Optimizer campaign trigger"
              })
            }), _jsx(_components.td, {
              children: "Trigger campaigns in Adobe Journey Optimizer"
            }), _jsx(_components.td, {
              children: "--"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.adobe.com/journey-optimizer-apis/references/messaging/",
                children: "Campaign trigger docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adobe-experience-platform",
        children: "Connect to Adobe Experience Platform"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Adobe Experience Platform"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Adobe Experience Platform"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Organization ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Sandbox name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client secret"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on how to obtain these values, refer to ", _jsx(_components.a, {
          href: "https://experienceleague.adobe.com/en/docs/experience-platform/landing/platform-apis/api-authentication",
          children: "Adobe's docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Adobe Experience Platform destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Adobe Experience Platform destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-datasets",
        children: "Syncing datasets"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to dataset records. Upsert mode is only available if your dataset has unified profile and upsert enabled. Refer to ", _jsx(_components.a, {
          href: "https://experienceleague.adobe.com/en/docs/experience-platform/catalog/datasets/enable-upsert",
          children: "Adobe's docs"
        }), " to enable these features."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to datasets in Adobe Experience Platform on any column in your model and any field in Adobe Experience Platform. Ensure the data types of the model column and Adobe Experience Platform field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Adobe Experience Platform automatically generates an\nidentifier for every new record synced, so there is no need to match an\nexisting record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync dataset fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the fields defined in the schema of your dataset.\nEnsure your model's columns have the same data types as the fields you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "trigger-campaigns-via-adobe-journey-optimizer",
        children: "Trigger campaigns via Adobe Journey Optimizer"
      }), "\n", _jsx(_components.p, {
        children: "Trigger push, sms, or emails to your Adobe Journey Optimizer campaigns."
      }), "\n", _jsx(_components.h4, {
        id: "campaign-id",
        children: "Campaign ID"
      }), "\n", _jsx(_components.p, {
        children: "Enter the ID of the campaign you want to trigger. Each sync can trigger sends to a single campaign."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You must specify which AEP profile namespace (e.g. ", _jsx(_components.code, {
          children: "email"
        }), ") you want to use to map your recipients in AEP and map the\n", _jsx(_components.code, {
          children: "userId"
        }), " to your source column to identify that namespace. You may also include other user attributes in the ", _jsx(_components.code, {
          children: "profile"
        }), " field."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can add dynamic content to your campaign through the ", _jsx(_components.code, {
          children: "context"
        }), " field.\nThe ", _jsx(_components.code, {
          children: "context"
        }), " field is an object of property-value pairs (e.g. ", _jsx(_components.code, {
          children: "{ \"subject\": \"Subject line\" }"
        }), ")."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["To use dynamic content in your campaign properly, you need to set up the\ntemplate to reference the context object in Adobe Journey Optimizer. Refer to\nthis\n", _jsx(_components.a, {
            href: "https://experienceleague.adobe.com/en/docs/journey-optimizer-learn/tutorials/create-campaigns/api-triggered-campaigns",
            children: "tutorial"
          }), "\non how to do so."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
